// src/utils/alertUtils.js

/**
 * Alert utilities for client data freshness monitoring
 * Used by both Firm Portal and User Portal
 */

export const DEFAULT_ALERT_THRESHOLDS = {
    strategicContextDays: 60,
    financialsDays: 90,
    asanaDays: 30
};

export const ALERT_TYPES = {
    STRATEGIC_CONTEXT: 'strategic_context',
    FINANCIALS: 'financials',
    ASANA: 'asana'
};

/**
 * Safely convert Firestore timestamp or Date to Date object
 */
const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value.toDate && typeof value.toDate === 'function') return value.toDate();
    if (typeof value === 'string' || typeof value === 'number') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
};

/**
 * Calculate days since a given date
 */
const daysSince = (date) => {
    if (!date) return Infinity;
    const now = new Date();
    const diffTime = now - date;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get the most recent strategic context date for a client
 * Checks multiple possible fields in priority order
 */
export function getStrategicContextDate(client) {
    if (!client) return null;
    
    // Priority: Strategic Pulse update > Questionnaire completion > Setup completion
    const strategicNarrativeDate = toDate(client.clientStrategicNarrativeUpdatedAt);
    const questionnaireDate = toDate(client.questionnaireCompletedAt);
    const setupDate = toDate(client.setupCompletedAt);
    
    // Return the most recent date
    const dates = [strategicNarrativeDate, questionnaireDate, setupDate].filter(d => d !== null);
    if (dates.length === 0) return null;
    
    return dates.reduce((latest, current) => current > latest ? current : latest);
}

/**
 * Get the most recent financials date for a client
 * Checks full financial summary date first, then ranges update date
 */
export function getFinancialsDate(client) {
    if (!client) return null;
    
    const financialsDate = toDate(client.financialSummaryLastUpdated);
    if (financialsDate) return financialsDate;
    
    // For ranges-only clients, use the ranges update timestamp
    const rangesDate = toDate(client.financialRangesUpdatedAt);
    return rangesDate;
}

/**
 * Get the most recent Asana sync date for a client
 * Only returns a date if Asana is actually connected
 */
export function getAsanaDate(client) {
    if (!client) return null;
    
    // Check for various Asana timestamp fields
    const asanaLastSync = client.asanaConnection?.lastSyncAt || 
                          client.asanaLastSyncAt ||
                          client.asanaOperationalBriefUpdatedAt;
    
    return toDate(asanaLastSync);
}

/**
 * Calculate alerts for a single client
 * 
 * @param {object} client - Client document data
 * @param {object} thresholds - Alert thresholds (strategicContextDays, financialsDays, asanaDays)
 * @param {object} dismissedAlerts - User's dismissed alerts object { [clientId]: { strategic, financials, asana } }
 * @returns {object} { hasAlerts, alerts, clientId, clientName }
 */
export function calculateClientAlerts(client, thresholds = {}, dismissedAlerts = {}) {
    if (!client || !client.id) {
        return { hasAlerts: false, alerts: [], clientId: null, clientName: null };
    }

    // Skip alerts for clients that aren't fully active yet
    // These include: inactive clients, clients pending admin review, clients still in setup
    const pendingStatuses = ['Pending Admin Review', 'Setup In Progress', 'Pending Activation'];
    if (client.isActive === false || pendingStatuses.includes(client.status)) {
        return { hasAlerts: false, alerts: [], clientId: client.id, clientName: client.name || client.officialCompanyName || 'Unknown Client' };
    }

    const alerts = [];
    const clientDismissals = dismissedAlerts[client.id] || {};
    
    // Merge with defaults
    const effectiveThresholds = {
        ...DEFAULT_ALERT_THRESHOLDS,
        ...thresholds
    };

    // Strategic Context Alert
    const strategicDate = getStrategicContextDate(client);
    const strategicDays = daysSince(strategicDate);
    
    if (strategicDays >= effectiveThresholds.strategicContextDays) {
        const dismissedAt = toDate(clientDismissals.strategic);
        const shouldShow = !dismissedAt || (strategicDate && dismissedAt < strategicDate);
        
        if (shouldShow || !dismissedAt) {
            alerts.push({
                type: ALERT_TYPES.STRATEGIC_CONTEXT,
                message: strategicDate 
                    ? `Strategic context is ${strategicDays} days old`
                    : 'Strategic context has never been set',
                lastUpdated: strategicDate,
                daysSince: strategicDays,
                threshold: effectiveThresholds.strategicContextDays,
                isDismissed: !!dismissedAt && !shouldShow
            });
        }
    }

   // Financials Alert — skip only for clients who intentionally skipped financials entirely
    const skipFinancialsAlert = client.financialsSkipped === true || 
                    client.financialIntelligenceTier === 'questionnaire_only';

    if (!skipFinancialsAlert) {
        const financialsDate = getFinancialsDate(client);
        const financialsDays = daysSince(financialsDate);
        const isRangesOnly = client.financialIntelligenceTier === 'ranges_only';
        
        if (financialsDays >= effectiveThresholds.financialsDays) {
            const dismissedAt = toDate(clientDismissals.financials);
            const shouldShow = !dismissedAt || (financialsDate && dismissedAt < financialsDate);
            
            if (shouldShow || !dismissedAt) {
                alerts.push({
                    type: ALERT_TYPES.FINANCIALS,
                    message: financialsDate 
                        ? isRangesOnly
                            ? `Financial ranges are ${financialsDays} days old. Update ranges.`
                            : `Financials are ${financialsDays} days old`
                        : isRangesOnly
                            ? 'Financial ranges have never been updated'
                            : 'Financials have never been uploaded',
                    lastUpdated: financialsDate,
                    daysSince: financialsDays,
                    threshold: effectiveThresholds.financialsDays,
                    isDismissed: !!dismissedAt && !shouldShow
                });
            }
        }
    }

    // Asana Alert (only if Asana is connected - has a sync date)
    const asanaDate = getAsanaDate(client);
    if (asanaDate) {
        const asanaDays = daysSince(asanaDate);
        
        if (asanaDays >= effectiveThresholds.asanaDays) {
            const dismissedAt = toDate(clientDismissals.asana);
            const shouldShow = !dismissedAt || (asanaDate && dismissedAt < asanaDate);
            
            if (shouldShow || !dismissedAt) {
                alerts.push({
                    type: ALERT_TYPES.ASANA,
                    message: `Asana data is ${asanaDays} days old`,
                    lastUpdated: asanaDate,
                    daysSince: asanaDays,
                    threshold: effectiveThresholds.asanaDays,
                    isDismissed: !!dismissedAt && !shouldShow
                });
            }
        }
    }

    // Filter out dismissed alerts for the final count
    const activeAlerts = alerts.filter(a => !a.isDismissed);

    return {
        hasAlerts: activeAlerts.length > 0,
        alerts: activeAlerts,
        allAlerts: alerts, // Include dismissed for reference if needed
        clientId: client.id,
        clientName: client.name || client.officialCompanyName || 'Unknown Client'
    };
}

/**
 * Get effective thresholds by merging firm settings with user overrides
 * 
 * @param {object} firmSettings - Firm-level settings (for enterprise users)
 * @param {object} userSettings - User-level settings
 * @returns {object} Merged thresholds
 */
export function getEffectiveThresholds(firmSettings, userSettings) {
    const firm = firmSettings || {};
    const user = userSettings || {};
    return {
        strategicContextDays: user.strategicContextDays ?? firm.strategicContextDays ?? DEFAULT_ALERT_THRESHOLDS.strategicContextDays,
        financialsDays: user.financialsDays ?? firm.financialsDays ?? DEFAULT_ALERT_THRESHOLDS.financialsDays,
        asanaDays: user.asanaDays ?? firm.asanaDays ?? DEFAULT_ALERT_THRESHOLDS.asanaDays
    };
}

/**
 * Format a date for display in alerts
 * 
 * @param {Date|object} date - Date object or Firestore timestamp
 * @returns {string} Formatted date string
 */
export function formatAlertDate(date) {
    if (!date) return 'Never';
    
    const d = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
    
    if (isNaN(d.getTime())) return 'Unknown';
    
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

/**
 * Calculate alerts for multiple clients at once
 * Used by Firm Portal to aggregate alerts across all clients
 * 
 * @param {Array} clients - Array of client documents
 * @param {object} thresholds - Alert thresholds
 * @param {object} dismissedAlerts - User's dismissed alerts
 * @returns {Array} Array of client alert results with alerts
 */
export function calculateAllClientAlerts(clients = [], thresholds = {}, dismissedAlerts = {}) {
    if (!clients || clients.length === 0) {
        return [];
    }
    
    const results = [];
    
    clients.forEach(client => {
        if (!client || !client.id) return;
        
        const clientResult = calculateClientAlerts(client, thresholds, dismissedAlerts);
        
        if (clientResult.hasAlerts) {
            results.push({
                ...clientResult,
                fcfoName: client.fcfoName,
                industry: client.industry
            });
        }
    });
    
    return results;
}